import api, { ApiResponse } from './axiosInstance';

export interface AssignmentTarget {
  type: 'asset' | 'equipment' | 'checkpoint' | 'task';
}
export interface Assignment {
  _id: string;
  projectId: string;
  assignmentType: 'asset' | 'equipment' | 'checkpoint' | 'task';
  assetId?: string;
  equipmentId?: string;
  checkpointId?: string;
  taskId?: string;
  currentAssigneeId: string;
  currentAssigneeName: string;
  currentAssigneeRole: 'Site Engineer' | 'Site Supervisor';
  assignedBy: string;
  assignedByName: string;
  assignmentDate: string;
  status: 'active' | 'completed' | 'cancelled' | 'reassigned';
  isActive: boolean;
  assignmentNotes?: string;
  priority?: 'Low' | 'Medium' | 'High' | 'Critical';
  expectedCompletionDate?: string;
  actualCompletionDate?: string;
  reassignmentCount: number;
  tasksGenerated: boolean;
  generatedTaskIds: string[];
  taskGenerationDate?: string;
  taskGenerationPeriod?: {
    startDate: string;
    endDate: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface AssignmentStats {
  projectId: string;
  totalAssets: number;
  totalEquipment: number;
  totalCheckpoints: number;
  totalTasks: number;
  assignedAssets: number;
  assignedEquipment: number;
  assignedCheckpoints: number;
  assignedTasks: number;
  unassignedAssets: number;
  unassignedEquipment: number;
  unassignedCheckpoints: number;
  unassignedTasks: number;
  assignmentPercentage: {
    assets: number;
    equipment: number;
    checkpoints: number;
    tasks: number;
    overall: number;
  };
  overdueAssignments: number;
  siteEngineersCount: number;
  siteSupervisorsCount: number;
  assignmentsByRole: {
    siteEngineers: number;
    siteSupervisors: number;
  };
}

export interface BulkAssignmentRequest {
  projectId: string;
  assignmentType: 'asset' | 'equipment' | 'checkpoint' | 'task';
  targetIds: string[];
  assigneeId: string;
  assigneeRole: 'Site Engineer' | 'Site Supervisor';
  assignmentNotes?: string;
  priority?: 'Low' | 'Medium' | 'High' | 'Critical';
  generateTasks?: boolean;
  taskGenerationPeriod?: {
    startDate: string;
    endDate: string;
  };
  overwriteExisting?: boolean;
  /** Backend includes every descendant (Equipment.parentId) under each target in the project — no need to load the full tree in the browser. */
  expandDescendants?: boolean;
}

export interface AssignmentValidationResult {
  isValid: boolean;
  warnings: string[];
  errors: string[];
  conflictingAssignments: Assignment[];
  overwriteRequired: boolean;
}

export interface Assignee {
  _id: string;
  name: string;
  email: string;
  role: {
    _id: string;
    name: string;
  };
}

export interface UnassignedItem {
  _id: string;
  name?: string;
  equipmentName?: string;
  equipmentId?: string;
  assetName?: string;
  assetNumber?: string;
}

export const assignmentService = {
  /**
   * Get assignments with filtering and pagination
   */
  async getAssignments(params: {
    projectId?: string;
    assignmentType?: string;
    assigneeId?: string;
    assigneeRole?: string;
    status?: string;
    isActive?: boolean;
    assetId?: string;
    equipmentId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
  } = {}) {
    // Ensure limit doesn't exceed API maximum
    const safeLimit = Math.min(params.limit || 100, 100);
    const response = await api.get<any>('/assignments', { 
      params: { ...params, limit: safeLimit } 
    });
    
    // Handle both success patterns: status="success" and success=true
    const isSuccess = response.data.status === 'success' || response.data.success === true;
    if (!isSuccess) {
      throw new Error(response.data.message || 'Failed to fetch assignments');
    }
    
    // Map the backend response to expected format
    const backendData = response.data.data;
    const mappedAssignments = (backendData.assignments || []).map((assignment: any) => ({
      _id: assignment.id,
      projectId: assignment.projectId?.id || assignment.projectId,
      assignmentType: 'equipment', // Default type
      targetId: assignment.userId?.id,
      assigneeId: assignment.userId?.id,
      assigneeName: assignment.userId?.name,
      assigneeRole: 'Site Engineer', // Default role
      assignmentDate: assignment.createdAt,
      status: assignment.isActive ? 'active' : 'inactive',
      isActive: assignment.isActive,
      workloadPercentage: assignment.workloadPercentage,
      startDate: assignment.startDate,
      createdAt: assignment.createdAt,
      createdBy: assignment.createdBy,
      updatedBy: assignment.updatedBy
    }));
    
    return {
      success: true,
      message: 'Assignments fetched successfully',
      data: mappedAssignments,
      pagination: backendData.pagination || {
        total: mappedAssignments.length,
        page: 1,
        limit: safeLimit,
        pages: 1
      }
    };
  },

  /**
   * Get assignment by ID
   */
  async getAssignmentById(assignmentId: string) {
    const response = await api.get<ApiResponse<Assignment>>(`/assignments/${assignmentId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch assignment');
    return response.data;
  },

  /**
   * Create bulk assignments
   */
  async createBulkAssignment(data: BulkAssignmentRequest) {
    const response = await api.post<
      ApiResponse<{
        equipmentAssigned?: number;
        tasksGenerated?: number;
        tasksSkipped?: number;
        tasksUpdated?: number;
        executionTime?: number;
        errors?: unknown[];
      }>
    >('/assignments/bulk', data);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to create assignments');
    return response.data;
  },

  /**
   * Assign equipment to site engineer/supervisor with automatic checkpoint task generation
   */
  async assignEquipmentWithCheckpoints(data: {
    projectId: string;
    equipmentIds: string[];
    assigneeId: string;
    assigneeRole: 'Site Engineer' | 'Site Supervisor';
    generateTasks: boolean;
    taskGenerationPeriod?: {
      startDate: string;
      endDate: string;
    };
    assignmentNotes?: string;
    priority?: 'Low' | 'Medium' | 'High' | 'Critical';
  }) {
    const response = await api.post<ApiResponse<{
      assignments: Assignment[];
      tasksGenerated: number;
      checkpointsAssigned: number;
    }>>('/assignments/equipment-with-checkpoints', data);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to assign equipment');
    return response.data;
  },

  /**
   * Get project equipment grouped by assets for assignment
   */
  async getProjectEquipmentByAssets(projectId: string): Promise<{
    assets: Array<{
      _id: string;
      name: string;
      specification: any;
      equipment: Array<{
        _id: string;
        equipmentName: string;
        equipmentId: string;
        makeAndModel: string;
        currentStatus: string;
        checkpointCount: number;
        isAssigned: boolean;
        assignedTo?: {
          name: string;
          role: string;
        };
      }>;
      totalEquipment: number;
      assignedEquipment: number;
    }>;
    summary: {
      totalAssets: number;
      totalEquipment: number;
      assignedEquipment: number;
      unassignedEquipment: number;
    };
  }> {
    const response = await api.get<ApiResponse<any>>(`/assignments/project/${projectId}/equipment-by-assets`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch project equipment');
    return response.data.data;
  },

  /**
   * Create single assignment
   */
  async createAssignment(data: {
    projectId: string;
    assignmentType: 'asset' | 'equipment' | 'checkpoint' | 'task';
    targetId: string;
    assigneeId: string;
    assigneeRole: 'Site Engineer' | 'Site Supervisor';
    assignmentNotes?: string;
    priority?: 'Low' | 'Medium' | 'High' | 'Critical';
    expectedCompletionDate?: string;
    generateTasks?: boolean;
    taskGenerationPeriod?: {
      startDate: string;
      endDate: string;
    };
  }) {
    const response = await api.post<ApiResponse<Assignment>>('/assignments', data);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to create assignment');
    return response.data;
  },

  /**
   * Validate bulk assignment request
   */
  async validateBulkAssignment(data: {
    projectId: string;
    assignmentType: 'asset' | 'equipment' | 'checkpoint' | 'task';
    targetIds: string[];
    assigneeId: string;
    assigneeRole: 'Site Engineer' | 'Site Supervisor';
    overwriteExisting?: boolean;
  }): Promise<AssignmentValidationResult> {
    const response = await api.post<ApiResponse<AssignmentValidationResult>>('/assignments/validate-bulk', data);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to validate assignment');
    return response.data.data;
  },

  /**
   * Update assignment
   */
  async updateAssignment(assignmentId: string, data: {
    assignmentNotes?: string;
    priority?: 'Low' | 'Medium' | 'High' | 'Critical';
    expectedCompletionDate?: string;
    status?: 'active' | 'completed' | 'cancelled';
  }) {
    const response = await api.patch<ApiResponse<Assignment>>(`/assignments/${assignmentId}`, data);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to update assignment');
    return response.data;
  },

  /**
   * Reassign assignment
   */
  async reassignAssignment(assignmentId: string, data: {
    newAssigneeId: string;
    reason?: string;
  }) {
    const response = await api.post<ApiResponse<Assignment>>(`/assignments/${assignmentId}/reassign`, data);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to reassign assignment');
    return response.data;
  },

  /**
   * Complete assignment
   */
  async completeAssignment(assignmentId: string, data: {
    completionNotes?: string;
  }) {
    const response = await api.post<ApiResponse<Assignment>>(`/assignments/${assignmentId}/complete`, data);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to complete assignment');
    return response.data;
  },

  /**
   * Delete assignment (soft delete)
   */
  async deleteAssignment(assignmentId: string) {
    const response = await api.delete<ApiResponse<void>>(`/assignments/${assignmentId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to delete assignment');
    return response.data;
  },

  /**
   * Get project assignment statistics
   */
  async getProjectStats(projectId: string): Promise<AssignmentStats> {
    const response = await api.get<any>(`/assignments/stats/${projectId}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch project stats');
    }
    
    // Map the response to the expected format
    const backendStats = response.data.data;
    const mappedStats: AssignmentStats = {
      projectId: projectId,
      totalAssets: backendStats.totalAssets || 0,
      totalEquipment: backendStats.totalEquipment || 0,
      totalCheckpoints: backendStats.totalCheckpoints || 0,
      totalTasks: backendStats.totalTasks || 0,
      assignedAssets: backendStats.assignedAssets || 0,
      assignedEquipment: backendStats.assignedEquipment || 0,
      assignedCheckpoints: backendStats.assignedCheckpoints || 0,
      assignedTasks: backendStats.assignedTasks || 0,
      unassignedAssets: backendStats.unassignedAssets || 0,
      unassignedEquipment: backendStats.unassignedEquipment || 0,
      unassignedCheckpoints: backendStats.unassignedCheckpoints || 0,
      unassignedTasks: backendStats.unassignedTasks || 0,
      assignmentPercentage: {
        assets: backendStats.assetAssignmentPercentage || 0,
        equipment: backendStats.equipmentAssignmentPercentage || 0,
        checkpoints: 0,
        tasks: backendStats.taskAssignmentPercentage || 0,
        overall: backendStats.overallAssignmentPercentage || 0
      },
      overdueAssignments: 0,
      siteEngineersCount: 0,
      siteSupervisorsCount: 0,
      assignmentsByRole: {
        siteEngineers: 0,
        siteSupervisors: 0
      }
    };
    
    return mappedStats;
  },

  /**
   * Get unassigned items for a project
   */
  async getUnassignedItems(projectId: string, itemType: 'asset' | 'equipment' | 'task'): Promise<{
    items: UnassignedItem[];
    count: number;
    itemType: string;
  }> {
    const response = await api.get<ApiResponse<{
      items: UnassignedItem[];
      count: number;
      itemType: string;
    }>>('/assignments/unassigned', {
      params: { projectId, itemType }
    });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch unassigned items');
    return response.data.data;
  },

  /**
   * Get available assignees (Site Engineers and Site Supervisors)
   * @deprecated Use getEngineersWithAssignments instead
   */
  async getAvailableAssignees(): Promise<{
    assignees: Assignee[];
    count: number;
  }> {
    const response = await api.get<ApiResponse<{
      assignees: Assignee[];
      count: number;
    }>>('/assignments/assignees');
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch assignees');
    return response.data.data;
  },

  /**
   * Get all site engineers and supervisors with their assignment counts
   */
  async getEngineersWithAssignments(queryParams?: {
    search?: string;
    projectId?: string;
  } | string): Promise<{
    engineers: Assignee[];
  }> {
    const params = new URLSearchParams();
    
    // Support both old string parameter and new object parameter for backward compatibility
    if (typeof queryParams === 'string') {
      if (queryParams) {
        params.append('search', queryParams);
      }
    } else if (queryParams && typeof queryParams === 'object') {
      if (queryParams.search) {
        params.append('search', queryParams.search);
      }
      if (queryParams.projectId) {
        params.append('projectId', queryParams.projectId);
      }
    }
    
    console.log('🔍 Calling engineers endpoint with params:', params.toString());
    
    const response = await api.get<ApiResponse<{
      engineers: Assignee[];
    }>>(`/assignments/engineers${params.toString() ? `?${params.toString()}` : ''}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch engineers');
    }
    
    return response.data.data;
  },

  /**
   * Get simple resource counts - projects and equipment only
   */
  async getResourceCounts(resourceId: string): Promise<{
    resourceId: string;
    resourceName: string;
    totalProjects: number;
    totalEquipment: number;
    projects: Array<{
      projectId: string;
      projectName: string;
      location: string;
      equipmentCount: number;
    }>;
  }> {
    const response = await api.get<ApiResponse<any>>(`/assignments/resource/${resourceId}/counts`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch resource counts');
    }
    return response.data.data;
  },

  /**
   * Get site engineer workload information
   */
  async getEngineerWorkload(engineerId: string): Promise<{
    engineerId: string;
    engineerName: string;
    totalProjects: number;
    totalEquipment: number;
    projects: Array<{
      projectId: string;
      projectName: string;
      location: string;
      startDate: string;
      endDate: string;
      equipmentCount: number;
      equipment: Array<{
        equipmentId: string;
        equipmentName: string;
        siteLabel: string;
        makeAndModel: string;
        currentStatus: string;
      }>;
      taskCounts: {
        totalDue: number;
        totalHold: number;
        totalOverdue: number;
        totalPass: number;
        totalFail: number;
        totalTasks: number;
      };
    }>;
  }> {
    const response = await api.get<ApiResponse<any>>(`/site-supervisor/engineer/${engineerId}/workload`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch engineer workload');
    }
    return response.data.data;
  },

  /**
   * Get comprehensive workload for any resource (engineer or supervisor)
   * Returns precise per-project equipment counts across all their projects
   */
  async getResourceWorkload(resourceId: string): Promise<{
    resourceId: string;
    resourceName: string;
    totalProjects: number;
    totalEquipment: number;
    projects: Array<{
      projectId: string;
      projectName: string;
      projectCode: string;
      equipmentCount: number;
      isCurrent?: boolean;
    }>;
  }> {
    const response = await api.get<ApiResponse<any>>(`/assignments/resource/${resourceId}/workload`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch resource workload');
    }
    return response.data.data;
  },

  /**
   * Sync assignments for a specific project
   * Creates Assignment documents for equipment with engineers/supervisors but no Assignment
   */
  async syncProjectAssignments(projectId: string) {
    const response = await api.post<ApiResponse<{
      success: boolean;
      created: number;
      updated: number;
      skipped: number;
      errors: string[];
      details: {
        engineers: number;
        supervisors: number;
      };
    }>>(`/assignments/sync/${projectId}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to sync assignments');
    }
    
    return response.data.data;
  },

  /**
   * Check sync status for a project
   * Returns count of equipment with/without assignments
   */
  async checkSyncStatus(projectId: string) {
    const response = await api.get<ApiResponse<{
      totalEquipment: number;
      withAssignments: number;
      withoutAssignments: number;
      needsSync: boolean;
    }>>(`/assignments/check-sync/${projectId}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to check sync status');
    }
    
    return response.data.data;
  }
};

export default assignmentService;
